import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import MetricsPanel from "../components/MetricsPanel";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import { formatDate, initials, ROLE_LABELS } from "../lib/utils";

const COUNTS = [
  { key: "submitted", label: "Submitted" },
  { key: "assigned", label: "Assigned" },
  { key: "completed", label: "Completed" },
];

export default function UserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    api
      .get(`/users/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e.response?.status;
        if (status === 403) setErr("You don't have access to this profile.");
        else if (status === 404) setErr("Not found.");
        else setErr("Failed to load.");
      });
  }, [id]);

  if (err) return <div className="surface-card p-8 text-center text-slate-500">{err}</div>;
  if (!data) return <div className="surface-card p-8 text-center text-slate-400">Loading…</div>;

  const { user, counts, metrics, recent_tickets } = data;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="surface-card p-5">
        <div className="flex items-center gap-4">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-semibold shrink-0">
              {initials(user.name)}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{user.name}</h1>
            <div className="text-sm text-slate-500 mt-0.5 font-mono">{user.email}</div>
            {user.role && (
              <div className="text-[11px] uppercase tracking-wide text-blue-600 font-semibold mt-1">
                {ROLE_LABELS[user.role] || user.role}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Count tiles */}
      <div className="grid grid-cols-3 gap-3">
        {COUNTS.map(({ key, label }) => (
          <div key={key} className="surface-card p-5 text-center">
            <div className="font-display text-3xl font-bold text-slate-900 tabular-nums tracking-tight">
              {counts?.[key] ?? 0}
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Metrics */}
      <MetricsPanel metrics={metrics} />

      {/* Recent tickets: Submitted */}
      <div className="surface-card p-4">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Submitted tickets</div>
        <div className="divide-y divide-slate-100">
          {(recent_tickets?.submitted || []).map((t) => (
            <Link
              key={t.id}
              to={`/tickets/${t.id}`}
              className="py-2 flex items-center justify-between gap-2 hover:bg-slate-50 -mx-2 px-2 rounded"
            >
              <span className="font-mono text-[11px] text-slate-500">{t.ticket_no}</span>
              <span className="flex-1 truncate text-slate-800">{t.subject}</span>
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} />
              <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(t.created_at)}</span>
            </Link>
          ))}
          {(recent_tickets?.submitted || []).length === 0 && (
            <div className="py-3 text-xs text-slate-400">No submitted tickets.</div>
          )}
        </div>
      </div>

      {/* Recent tickets: Assigned */}
      <div className="surface-card p-4">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Assigned tickets</div>
        <div className="divide-y divide-slate-100">
          {(recent_tickets?.assigned || []).map((t) => (
            <Link
              key={t.id}
              to={`/tickets/${t.id}`}
              className="py-2 flex items-center justify-between gap-2 hover:bg-slate-50 -mx-2 px-2 rounded"
            >
              <span className="font-mono text-[11px] text-slate-500">{t.ticket_no}</span>
              <span className="flex-1 truncate text-slate-800">{t.subject}</span>
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} />
              <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(t.created_at)}</span>
            </Link>
          ))}
          {(recent_tickets?.assigned || []).length === 0 && (
            <div className="py-3 text-xs text-slate-400">No assigned tickets.</div>
          )}
        </div>
      </div>
    </div>
  );
}
